import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { DEV_TOOLS_ENABLED } from './lib/dev-tools-enabled';
import { NOT_FOUND_MARKER, isFlightPath, isKnownPath, normalizePath } from './lib/site-routes';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Marketing root element is missing.');

const app = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

// Pages are prerendered at build time (scripts/prerender.mjs), which stamps the
// route it rendered on #root[data-ssr]. Hydrate only when the client is certain
// to produce the same markup:
//  - flight routes are never hydrated: the Dawn/Night variant comes from
//    browser-only state, and their prerendered copy is visually hidden, so the
//    client render replacing it is invisible;
//  - dev-mode tooling (localhost/staging) renders nothing on its first pass;
//  - the 404 page is hydrated only for paths the router really treats as 404.
// Everything else falls back to a plain client render, as before prerendering.
function shouldHydrate(): boolean {
  const rendered = root?.dataset.ssr;
  if (!rendered || DEV_TOOLS_ENABLED) return false;
  const path = normalizePath(window.location.pathname);
  if (isFlightPath(path)) return false;
  if (rendered === NOT_FOUND_MARKER) return !isKnownPath(path);
  return rendered === path;
}

if (shouldHydrate()) {
  hydrateRoot(root, app);
} else {
  createRoot(root).render(app);
}
